import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  FormHelperText,
  Alert,
  Stack,
  Divider,
  Avatar,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Flag as PriorityIcon,
  Schedule as ClockIcon,
  Label as TagIcon,
} from '@mui/icons-material';

// Mock team members for assignee selection
const teamMembers = [
  { id: 1, name: 'Mike Johnson', avatar: 'MJ', role: 'Lead Installer', color: '#9c27b0' },
  { id: 2, name: 'Clayton Smith', avatar: 'CS', role: 'Senior Technician', color: '#607d8b' },
  { id: 3, name: 'David Wilson', avatar: 'DW', role: 'Network Engineer', color: '#e91e63' },
  { id: 4, name: 'Sarah Chen', avatar: 'SC', role: 'Project Coordinator', color: '#ff9800' },
  { id: 5, name: 'Tom Anderson', avatar: 'TA', role: 'Junior Technician', color: '#4caf50' },
  { id: 6, name: 'Unassigned', avatar: 'U', role: '', color: '#9e9e9e' },
];

const taskTypes = [
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

const priorityOptions = [
  { value: 'low', label: 'Low', color: '#4caf50' },
  { value: 'medium', label: 'Medium', color: '#ff9800' },
  { value: 'high', label: 'High', color: '#ff5722' },
  { value: 'critical', label: 'Critical', color: '#f44336' },
];

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'blocked', label: 'Blocked' },
];

const tagSuggestions = [
  'setup', 'installation', 'network', 'testing', 'documentation',
  'critical', 'urgent', 'maintenance', 'building-a', 'building-b',
  'cameras', 'infrastructure', 'preparation', 'configuration'
];

export default function TaskFormDialog({
  open,
  onClose,
  task = null,
  onSave,
  allTasks = [],
  parentTask = null
}) {
  const isEditMode = !!task;
  const isSubtask = !!parentTask;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    assignee: null,
    dueDate: null,
    estimatedHours: '',
    actualHours: 0,
    progress: 0,
    taskType: 'internal',
    tags: [],
    dependencies: [],
    parentTaskId: parentTask?.id || null,
    addSubtasksLater: false,
    subtasks: [],
  });

  const [errors, setErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        ...task,
        assignee: task.assignee || null,
        dueDate: task.dueDate ? new Date(task.dueDate) : null,
        taskType: task.taskType || 'internal',
        estimatedHours: task.estimatedHours || '',
        tags: task.tags || [],
        dependencies: task.dependencies || [],
        addSubtasksLater: false,
        subtasks: task.subtasks || [],
      });
      // Show advanced section if task has these fields populated
      setShowAdvanced(!!(task.tags?.length || task.dependencies?.length || task.estimatedHours));
    } else {
      // Reset form for new task
      setFormData({
        title: '',
        description: '',
        status: 'pending',
        priority: 'medium',
        assignee: null,
        dueDate: null,
        estimatedHours: '',
        actualHours: 0,
        progress: 0,
        taskType: 'internal',
        tags: [],
        dependencies: [],
        parentTaskId: parentTask?.id || null,
        addSubtasksLater: false,
        subtasks: [],
      });
      setShowAdvanced(false);
    }
    setErrors({});
  }, [task, parentTask]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    // Auto-update progress based on status (only for editing)
    if (field === 'status' && isEditMode) {
      if (value === 'completed') {
        setFormData(prev => ({ ...prev, progress: 100 }));
      } else if (value === 'pending') {
        setFormData(prev => ({ ...prev, progress: 0 }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.assignee) {
      newErrors.assignee = 'Please assign this task';
    }

    if (formData.status === 'blocked' && !formData.blockedBy) {
      newErrors.blockedBy = 'Please provide a reason for blocking';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const taskData = {
        ...formData,
        id: task?.id || Date.now(),
        dueDate: formData.dueDate ? formData.dueDate.toISOString().split('T')[0] : null,
        createdDate: task?.createdDate || new Date().toISOString().split('T')[0],
        updatedDate: new Date().toISOString(),
        actualHours: isEditMode ? formData.actualHours : 0,
        progress: isEditMode ? formData.progress : 0,
        completedDate: formData.status === 'completed' && !task?.completedDate
          ? new Date().toISOString().split('T')[0]
          : task?.completedDate,
        // Include subtasks with proper formatting
        subtasks: formData.subtasks.filter(subtask => subtask.title.trim()).map(subtask => ({
          ...subtask,
          dueDate: subtask.dueDate ? subtask.dueDate.toISOString().split('T')[0] : null,
          createdDate: new Date().toISOString().split('T')[0],
          comments: [],
          attachments: 0,
          tags: [],
        })),
      };

      onSave(taskData);
      onClose();
    }
  };

  const handleAddTag = (event) => {
    if (event.key === 'Enter' && event.target.value.trim()) {
      const newTag = event.target.value.trim();
      if (!formData.tags.includes(newTag)) {
        handleChange('tags', [...formData.tags, newTag]);
      }
      event.target.value = '';
      event.preventDefault();
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    handleChange('tags', formData.tags.filter(tag => tag !== tagToRemove));
  };

  const handleAddSubtask = () => {
    const newSubtask = {
      id: Date.now(),
      title: '',
      assignee: formData.assignee, // Default to parent's assignee
      priority: 'medium',
      dueDate: formData.dueDate,
      estimatedHours: '',
      status: 'pending',
      progress: 0,
      actualHours: 0,
    };
    handleChange('subtasks', [...formData.subtasks, newSubtask]);
  };

  const handleUpdateSubtask = (subtaskId, field, value) => {
    const updatedSubtasks = formData.subtasks.map(subtask =>
      subtask.id === subtaskId ? { ...subtask, [field]: value } : subtask
    );
    handleChange('subtasks', updatedSubtasks);
  };

  const handleRemoveSubtask = (subtaskId) => {
    handleChange('subtasks', formData.subtasks.filter(subtask => subtask.id !== subtaskId));
  };

  const getAvailableDependencies = () => {
    // Filter out current task and its parent
    return allTasks.filter(t =>
      t.id !== formData.id &&
      t.id !== formData.parentTaskId
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">
              {isEditMode ? 'Edit Task' : 'Create New Task'}
            </Typography>
            {isSubtask && (
              <Typography variant="caption" color="text.secondary">
                Subtask of: {parentTask.title}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Title & Description */}
          <TextField
            label="Title"
            fullWidth
            required
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            error={!!errors.title}
            helperText={errors.title}
            autoFocus
            placeholder="What needs to be done?"
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Add more details about this task..."
          />

          <Divider />

          {/* Main row: Assignee, Due Date, Priority */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required error={!!errors.assignee}>
                <InputLabel id="assignee-label">Assignee</InputLabel>
                <Select
                  labelId="assignee-label"
                  value={formData.assignee?.id || ''}
                  label="Assignee"
                  onChange={(e) => {
                    const member = teamMembers.find(m => m.id === e.target.value);
                    handleChange('assignee', member);
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        minWidth: '280px',
                        maxWidth: '400px'
                      }
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      minHeight: '1.4375em',
                      paddingRight: '32px',
                      minWidth: '200px'
                    }
                  }}
                  renderValue={(selected) => {
                    const member = teamMembers.find(m => m.id === selected);
                    return member ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 20, height: 20, bgcolor: member.color, fontSize: '0.7rem' }}>
                          {member.avatar}
                        </Avatar>
                        <Typography variant="body2" noWrap>{member.name}</Typography>
                      </Box>
                    ) : '';
                  }}
                >
                  {teamMembers.map(member => (
                    <MenuItem key={member.id} value={member.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: member.color, fontSize: '0.75rem' }}>
                          {member.avatar}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{member.name}</Typography>
                          {member.role && (
                            <Typography variant="caption" color="text.secondary">
                              {member.role}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.assignee && <FormHelperText>{errors.assignee}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Due Date"
                type="date"
                fullWidth
                value={formData.dueDate ? new Date(formData.dueDate).toISOString().split('T')[0] : ''}
                onChange={(e) => handleChange('dueDate', e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => handleChange('priority', e.target.value)}
                >
                  {priorityOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PriorityIcon sx={{ color: option.color, fontSize: 18 }} />
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Second row: Status, Est Hours, Type */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  {statusOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Est. Hours"
                type="number"
                fullWidth
                value={formData.estimatedHours}
                onChange={(e) => handleChange('estimatedHours', e.target.value)}
                placeholder="0"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ClockIcon sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, step: 0.5 }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.taskType}
                  label="Type"
                  onChange={(e) => handleChange('taskType', e.target.value)}
                >
                  {taskTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Blocked reason (conditional) */}
          {formData.status === 'blocked' && (
            <TextField
              label="Blocked Reason"
              fullWidth
              required
              value={formData.blockedBy || ''}
              onChange={(e) => handleChange('blockedBy', e.target.value)}
              error={!!errors.blockedBy}
              helperText={errors.blockedBy}
              placeholder="Why is this task blocked?"
            />
          )}

          {/* Advanced Options Toggle */}
          <Box>
            <Button
              size="small"
              onClick={() => setShowAdvanced(!showAdvanced)}
              sx={{ textTransform: 'none' }}
            >
              {showAdvanced ? '− Hide' : '+ Show'} Advanced Options
            </Button>
          </Box>

          {/* Advanced Options */}
          {showAdvanced && (
            <>
              <Divider />

              {/* Tags */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onDelete={() => handleRemoveTag(tag)}
                    />
                  ))}
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Press Enter to add tags"
                  onKeyDown={handleAddTag}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TagIcon sx={{ fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Dependencies */}
              <Autocomplete
                multiple
                options={getAvailableDependencies()}
                getOptionLabel={(option) => option.title}
                value={allTasks.filter(t => formData.dependencies.includes(t.id))}
                onChange={(e, newValue) => {
                  handleChange('dependencies', newValue.map(v => v.id));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Dependencies"
                    placeholder="Select tasks that must be completed first"
                    helperText="This task will be blocked until dependencies are complete"
                  />
                )}
              />
            </>
          )}

          {/* Progress and Actual Hours (only for editing) */}
          {isEditMode && (
            <>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography gutterBottom>
                    Progress: {formData.progress}%
                  </Typography>
                  <Box sx={{ px: 1 }}>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.progress}
                      onChange={(e) => handleChange('progress', parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                  </Box>
                  {formData.status === 'pending' && formData.progress > 0 && (
                    <FormHelperText>Progress should be 0% for pending tasks</FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Actual Hours"
                    type="number"
                    fullWidth
                    value={formData.actualHours}
                    onChange={(e) => handleChange('actualHours', parseFloat(e.target.value) || 0)}
                    InputProps={{
                      inputProps: { min: 0, step: 0.5 }
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {/* Subtasks Section (only for new tasks that aren't subtasks themselves) */}
          {!isEditMode && !isSubtask && (
            <>
              <Divider />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2">
                    Subtasks ({formData.subtasks.length})
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddSubtask}
                    sx={{ textTransform: 'none' }}
                  >
                    Add Subtask
                  </Button>
                </Box>

                {formData.subtasks.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No subtasks yet. Click "Add Subtask" to break this task into smaller parts.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Subtask Title</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>Assignee</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Priority</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Due Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.subtasks.map((subtask, index) => (
                          <TableRow key={subtask.id}>
                            <TableCell>
                              <TextField
                                fullWidth
                                size="small"
                                variant="standard"
                                placeholder={`Subtask ${index + 1} title...`}
                                value={subtask.title}
                                onChange={(e) => handleUpdateSubtask(subtask.id, 'title', e.target.value)}
                                InputProps={{
                                  disableUnderline: !subtask.title,
                                  sx: { fontSize: '0.875rem' }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <FormControl fullWidth size="small" variant="standard">
                                <Select
                                  value={subtask.assignee?.id || ''}
                                  onChange={(e) => {
                                    const member = teamMembers.find(m => m.id === e.target.value);
                                    handleUpdateSubtask(subtask.id, 'assignee', member);
                                  }}
                                  displayEmpty
                                  disableUnderline
                                  renderValue={(selected) => {
                                    const member = teamMembers.find(m => m.id === selected);
                                    return member ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar sx={{ width: 20, height: 20, bgcolor: member.color, fontSize: '0.7rem' }}>
                                          {member.avatar}
                                        </Avatar>
                                        <Typography variant="body2">{member.name}</Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">Select...</Typography>
                                    );
                                  }}
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  {teamMembers.map(member => (
                                    <MenuItem key={member.id} value={member.id}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar sx={{ width: 20, height: 20, bgcolor: member.color, fontSize: '0.7rem' }}>
                                          {member.avatar}
                                        </Avatar>
                                        <Typography variant="body2">{member.name}</Typography>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <FormControl fullWidth size="small" variant="standard">
                                <Select
                                  value={subtask.priority}
                                  onChange={(e) => handleUpdateSubtask(subtask.id, 'priority', e.target.value)}
                                  disableUnderline
                                  sx={{
                                    fontSize: '0.875rem',
                                    color: priorityOptions.find(p => p.value === subtask.priority)?.color
                                  }}
                                >
                                  {priorityOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PriorityIcon sx={{ color: option.color, fontSize: 16 }} />
                                        <Typography variant="body2">{option.label}</Typography>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                size="small"
                                variant="standard"
                                type="date"
                                value={subtask.dueDate ? new Date(subtask.dueDate).toISOString().split('T')[0] : ''}
                                onChange={(e) => handleUpdateSubtask(subtask.id, 'dueDate', e.target.value ? new Date(e.target.value) : null)}
                                InputProps={{
                                  disableUnderline: !subtask.dueDate,
                                  sx: { fontSize: '0.875rem' }
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveSubtask(subtask.id)}
                                color="error"
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </>
          )}

          {/* Info Alert for new tasks */}
          {!isEditMode && (
            <Alert severity="info" icon={false}>
              <Typography variant="caption">
                💡 Tip: Add subtasks above to break work into smaller parts, or add attachments and comments after creating the task.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!formData.title.trim()}
        >
          {isEditMode ? 'Save Changes' : 'Create Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}